import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { secureAuthService } from '../services/secureAuthService';

interface RateLimitContextType {
  aiChatCount: number;
  isAiChatLimited: boolean;
  incrementAiChatCount: () => Promise<boolean>;
  resetAiChatCount: () => Promise<void>;
  getRemainingCalls: () => number;
  isLoading: boolean;
  isAdmin: boolean; // Added admin status
}

const RateLimitContext = createContext<RateLimitContextType | undefined>(undefined);

export const useRateLimit = () => {
  const context = useContext(RateLimitContext);
  if (context === undefined) {
    throw new Error('useRateLimit must be used within a RateLimitProvider');
  }
  return context;
};

interface RateLimitProviderProps {
  children: ReactNode;
}

// Rate limits
const LIFETIME_USER_LIMIT = 5; // All users get 5 AI chats TOTAL (lifetime)

// Admin configuration
const ADMIN_EMAIL = 'adhiraj.msm@gmail.com'; // Admin user with unlimited access
const ADMIN_LIMIT = 999999; // Effectively unlimited for admin

export const RateLimitProvider: React.FC<RateLimitProviderProps> = ({ children }) => {
  const { isAuthenticated, user, supabaseUser, userProfile, refreshProfile } = useAuth();
  const [aiChatCount, setAiChatCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Check if current user is admin
  const isAdminUser = () => {
    if (!isAuthenticated || !user) return false;
    
    // Check both Supabase user email and local user email
    const userEmail = supabaseUser?.email || user?.email;
    return userEmail === ADMIN_EMAIL;
  };

  // Get current limit based on user plan
  const getCurrentLimit = () => {
    if (!isAuthenticated) {
      return 0; // Anonymous users cannot use AI chat
    }
    
    // Admin gets unlimited access
    if (isAdminUser()) {
      return ADMIN_LIMIT;
    }
    
    // Everyone else gets 5 total requests (lifetime limit)
    return LIFETIME_USER_LIMIT;
  };

  // Load count from Supabase or localStorage on mount and when auth changes
  useEffect(() => {
    console.log('🔄 RateLimit useEffect triggered:', {
      isAuthenticated,
      userId: user?.id,
      hasSupabaseUser: !!supabaseUser,
      hasUserProfile: !!userProfile
    });
    loadAiChatCount();
  }, [isAuthenticated, user, userProfile]);

  const loadAiChatCount = async () => {
    if (!isAuthenticated || !user) {
      console.log('🚫 Rate limit: Not authenticated or no user');
      setAiChatCount(0);
      return;
    }

    // Admin users don't need to track count
    if (isAdminUser()) {
      console.log('🔑 Admin user detected - setting count to 0 (unlimited access)');
      setAiChatCount(0);
      return;
    }

    console.log('🔍 Rate limit: Loading AI chat count for user:', user.id);

    try {
      if (secureAuthService.isConfigured() && supabaseUser) {
        console.log('✅ Rate limit: Using Supabase for lifetime rate limiting');
        // Use Supabase profile to store lifetime count
        const { canChat, remaining } = await secureAuthService.checkAIChatLimit(
          supabaseUser.id, 
          getCurrentLimit()
        );
        
        const currentCount = getCurrentLimit() - remaining;
        console.log('📊 Rate limit: Supabase result:', { canChat, remaining, currentCount, limit: getCurrentLimit() });
        setAiChatCount(currentCount);
      } else {
        console.log('💾 Rate limit: Using localStorage for lifetime rate limiting');
        // Use localStorage for lifetime count (no daily reset)
        const storageKey = `ai-chat-lifetime-count-${user.id}`;
        const savedCount = localStorage.getItem(storageKey);
        
        if (savedCount) {
          try {
            const count = parseInt(savedCount, 10);
            console.log('📊 Rate limit: localStorage lifetime count:', count);
            setAiChatCount(isNaN(count) ? 0 : count);
          } catch (error) {
            console.error('❌ Rate limit: Error parsing AI chat count:', error);
            setAiChatCount(0);
          }
        } else {
          console.log('📊 Rate limit: No saved count, starting at 0');
          setAiChatCount(0);
        }
      }
    } catch (error) {
      console.error('❌ Rate limit: Error loading AI chat count:', error);
      setAiChatCount(0);
    }
  };

  const incrementAiChatCount = async (): Promise<boolean> => {
    console.log('🔄 incrementAiChatCount called:', {
      isAuthenticated,
      userId: user?.id,
      currentCount: aiChatCount,
      isAdmin: isAdminUser()
    });

    if (!isAuthenticated || !user) {
      console.log('❌ Not authenticated or no user');
      return false;
    }

    // Admin users bypass rate limiting
    if (isAdminUser()) {
      console.log('🔑 Admin user detected - bypassing rate limit');
      return true;
    }

    const currentLimit = getCurrentLimit();
    
    // Check if user has reached their lifetime limit
    if (aiChatCount >= currentLimit) {
      console.log('🚫 Lifetime rate limit reached:', aiChatCount, '>=', currentLimit);
      return false; // Rate limit exceeded
    }

    setIsLoading(true);
    console.log('⏳ Starting rate limit increment...');

    try {
      if (secureAuthService.isConfigured() && supabaseUser) {
        console.log('☁️ Using Supabase to increment lifetime count');
        // Use Supabase to increment lifetime count
        const success = await secureAuthService.updateAIChatCount(supabaseUser.id, 1);
        
        if (success) {
          const newCount = aiChatCount + 1;
          setAiChatCount(newCount);
          console.log('✅ Supabase increment successful, new count:', newCount);
          // Refresh user profile to get updated count
          await refreshProfile();
          return true;
        }
        
        console.log('❌ Supabase increment failed');
        return false;
      } else {
        console.log('💾 Using localStorage to increment lifetime count');
        // Use localStorage for lifetime count
        const newCount = aiChatCount + 1;
        setAiChatCount(newCount);
        localStorage.setItem(`ai-chat-lifetime-count-${user.id}`, newCount.toString());
        console.log('✅ localStorage increment successful, new count:', newCount);
        return true;
      }
    } catch (error) {
      console.error('❌ Error incrementing AI chat count:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetAiChatCount = async (): Promise<void> => {
    if (!isAuthenticated || !user) {
      return;
    }

    setIsLoading(true);

    try {
      if (secureAuthService.isConfigured() && supabaseUser) {
        // Reset lifetime count in Supabase
        await secureAuthService.updateUserProfile(supabaseUser.id, {
          ai_chat_count: 0,
          ai_chat_last_reset: new Date().toISOString()
        });
        
        setAiChatCount(0);
        await refreshProfile();
      } else {
        // Reset lifetime count in localStorage
        setAiChatCount(0);
        localStorage.setItem(`ai-chat-lifetime-count-${user.id}`, '0');
        console.log('📊 Reset lifetime count to 0');
      }
    } catch (error) {
      console.error('Error resetting AI chat count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRemainingCalls = (): number => {
    // Admin users have unlimited calls
    if (isAdminUser()) {
      console.log('🔑 Admin user - unlimited calls');
      return ADMIN_LIMIT;
    }
    
    // For all other users, use lifetime limit of 5
    const currentLimit = getCurrentLimit();
    const remaining = Math.max(0, currentLimit - aiChatCount);
    console.log('📊 Rate limit calculation:', {
      currentLimit,
      aiChatCount,
      remaining,
      isAdmin: isAdminUser(),
      userEmail: user?.email
    });
    return remaining;
  };

  // Check if AI chat is limited based on lifetime usage
  const getIsAiChatLimited = (): boolean => {
    // Admin users are never limited
    if (isAdminUser()) {
      return false;
    }
    
    // Check against lifetime limit
    const currentLimit = getCurrentLimit();
    return aiChatCount >= currentLimit;
  };

  const isAiChatLimited = getIsAiChatLimited();

  const value: RateLimitContextType = {
    aiChatCount,
    isAiChatLimited,
    incrementAiChatCount,
    resetAiChatCount,
    getRemainingCalls,
    isLoading,
    isAdmin: isAdminUser()
  };

  return (
    <RateLimitContext.Provider value={value}>
      {children}
    </RateLimitContext.Provider>
  );
}; 